/**
 * UTM-only campaign context for FINAL leads.
 *
 * Purpose: when a lead is durably saved, tell the sales team which campaign
 * the visitor arrived from — nothing more.
 *
 * Hard boundaries:
 *   - ONLY the five standard UTM parameters, each strictly validated and
 *     bounded to 100 characters. No other query parameters, no raw URL, no
 *     query string, no hash, no contact data.
 *   - Raw click identifiers (`gclid` / `gbraid` / `wbraid`) are NOT captured,
 *     NOT persisted, and never sent to the CRM, the database, or an email in
 *     this release: offline conversion imports are not configured, so storing
 *     them would create ad-identifier records with no purpose. Google's own
 *     online tag continues to handle them in the browser.
 *   - Advertising permission is required BOTH at capture time and again at
 *     submission time (consent granted, no GPC, no device opt-out). There is
 *     no pre-consent buffer: attribution seen while denied is simply lost.
 *   - Storage is `sessionStorage` only, expires 24 hours after capture, and is
 *     cleared on denial, withdrawal, GPC, or a failed validation.
 *   - A new tagged landing REPLACES the whole stored context; internal
 *     navigation (no UTM parameters present) preserves it.
 *   - Only a landing path that is a known public route is stored.
 *   - A malformed record is dropped silently; it must never fail a quote or
 *     block a normal inquiry.
 */

import {
  getConsent,
  advertisingSuppressed,
  isConsentInitialized,
  CONSENT_SCHEMA_VERSION,
} from "./consent";
import { isPublicTrackableRoute } from "./public-routes";

const STORAGE_KEY = "mb:lead-attribution:v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_VALUE = 100;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
] as const;
export type UtmKey = (typeof UTM_KEYS)[number];

/** Conservative: letters, digits, dot, dash, underscore, plus, space. */
const UTM_VALUE_RE = /^[A-Za-z0-9._+\- ]{1,100}$/;
const EMAIL_RE = /@/;
const PHONE_RE = /(?:\+?\d[\s\-().]*){7,}/;

/**
 * Google Ads ValueTrack substitutions render as BARE numeric identifiers —
 * `{campaignid}` → `24284648560`, `{creative}` → `825622130838`. The generic
 * `PHONE_RE` guard treats any run of 7+ digits as contact data, so before this
 * allowance every real Google campaign and creative id was silently dropped
 * and the CRM saw a campaign line with only source/medium.
 *
 * The allowance is deliberately narrow:
 *   - it applies ONLY to the three fields Google fills with opaque account
 *     identifiers (`utm_campaign`, `utm_content`, `utm_id`). `utm_source` and
 *     `utm_medium` are human labels, and `utm_term` carries the visitor's own
 *     SEARCH KEYWORD — a person can search for a phone number, so all three
 *     keep the strict PII rejection;
 *   - the value must be digits ONLY and bounded to 20 characters. Anything
 *     with a `+`, spaces, dashes, dots or parentheses — i.e. every shape a
 *     formatted phone number takes — still fails and is dropped;
 *   - these are ACCOUNT identifiers, not click identifiers. `gclid`,
 *     `gbraid` and `wbraid` remain uncaptured and unpersisted.
 */
const NUMERIC_ID_RE = /^\d{1,20}$/;
const NUMERIC_ID_FIELDS: ReadonlySet<string> = new Set([
  "utm_campaign",
  "utm_content",
  "utm_id",
]);

/** True when a bounded, already-shape-checked value may be kept for this key. */
function utmValueAllowed(key: UtmKey, value: string): boolean {
  if (!UTM_VALUE_RE.test(value)) return false;
  if (NUMERIC_ID_FIELDS.has(key) && NUMERIC_ID_RE.test(value)) return true;
  if (EMAIL_RE.test(value) || PHONE_RE.test(value)) return false;
  return true;
}

export interface LeadAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  /** Google Ads `{campaignid}` when the approved tracking suffix supplies it. */
  utm_id?: string;
  /** Safe, known public landing path (no query, no hash). */
  landing_path: string;
  /** ISO timestamp of capture. */
  captured_at: string;
  /** Client-reported consent schema version at capture. */
  consent_version: number;
  /** Client-reported consent decision timestamp at capture. */
  consent_at: string;
}

/**
 * True while consent has NOT been initialized yet. In that window
 * `getConsent()` still reports the all-denied default, which is an UNKNOWN
 * state, not a refusal — nothing may be erased on the strength of it.
 */
function consentUnknown(): boolean {
  try {
    return !isConsentInitialized();
  } catch {
    return true;
  }
}

/** True when advertising context may be captured or attached right now. */
export function attributionAllowed(): boolean {
  try {
    const c = getConsent();
    if (!c?.advertising) return false;
    if (c.gpc === true) return false;
    if (advertisingSuppressed()) return false;
    return true;
  } catch {
    return false;
  }
}

function readSession(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearLeadAttribution(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

function validate(value: unknown): LeadAttribution | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const landing = raw.landing_path;
  if (typeof landing !== "string" || !landing.startsWith("/")) return null;
  if (landing.includes("?") || landing.includes("#")) return null;
  if (!isPublicTrackableRoute(landing)) return null;

  const capturedAt = raw.captured_at;
  if (typeof capturedAt !== "string") return null;
  const ts = Date.parse(capturedAt);
  if (!Number.isFinite(ts)) return null;
  if (Date.now() - ts > MAX_AGE_MS) return null;
  if (ts - Date.now() > 60_000) return null; // clock skew / tampering

  const consentAt = typeof raw.consent_at === "string" ? raw.consent_at : "";
  if (consentAt && !Number.isFinite(Date.parse(consentAt))) return null;
  const consentVersion =
    typeof raw.consent_version === "number" && Number.isFinite(raw.consent_version)
      ? raw.consent_version
      : CONSENT_SCHEMA_VERSION;

  const out: LeadAttribution = {
    landing_path: landing.slice(0, 200),
    captured_at: capturedAt,
    consent_version: consentVersion,
    consent_at: consentAt,
  };
  let hasUtm = false;
  for (const k of UTM_KEYS) {
    const v = raw[k];
    if (typeof v !== "string") continue;
    const trimmed = v.trim();
    if (!trimmed || trimmed.length > MAX_VALUE) continue;
    if (!utmValueAllowed(k, trimmed)) continue;
    out[k] = trimmed;
    hasUtm = true;
  }
  if (!hasUtm) return null;
  return out;
}

/** Extract validated UTMs from a query string. Returns {} when none are safe. */
export function parseUtm(search: string): Partial<Record<UtmKey, string>> {
  const out: Partial<Record<UtmKey, string>> = {};
  try {
    const params = new URLSearchParams(search || "");
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (!v) continue;
      const trimmed = v.trim();
      if (!trimmed || trimmed.length > MAX_VALUE) continue;
      if (!utmValueAllowed(k, trimmed)) continue;
      out[k] = trimmed;
    }
  } catch {
    /* noop */
  }
  return out;
}

/**
 * Capture campaign context for the current route.
 *
 * - denied / GPC / opted-out → clears any stored context and returns null;
 * - private or unknown route → never captured;
 * - tagged landing → replaces the entire stored context;
 * - untagged internal navigation → leaves an existing context untouched.
 */
export function captureLeadAttribution(pathname: string, search: string): LeadAttribution | null {
  if (typeof window === "undefined") return null;
  // Consent not decided yet: neither capture nor CLEAR. Wiping here is how a
  // stored campaign context used to disappear on a reload before the lazy
  // banner had initialized consent.
  if (consentUnknown()) return null;
  if (!attributionAllowed()) {
    clearLeadAttribution();
    return null;
  }
  if (!isPublicTrackableRoute(pathname)) return readLeadAttribution();

  const utm = parseUtm(search);
  if (Object.keys(utm).length === 0) return readLeadAttribution();

  const consent = getConsent();
  const record: LeadAttribution = {
    ...utm,
    landing_path: pathname.split(/[?#]/)[0].slice(0, 200),
    captured_at: new Date().toISOString(),
    consent_version: consent?.version ?? CONSENT_SCHEMA_VERSION,
    consent_at: consent?.timestamp ?? "",
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* private mode — attribution is best-effort, never required */
  }
  return record;
}

/** Read the stored context, re-checking permission and expiry. */
export function readLeadAttribution(): LeadAttribution | null {
  if (typeof window === "undefined") return null;
  if (consentUnknown()) return null;
  if (!attributionAllowed()) {
    clearLeadAttribution();
    return null;
  }
  const raw = readSession();
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearLeadAttribution();
    return null;
  }
  const valid = validate(parsed);
  if (!valid) {
    clearLeadAttribution();
    return null;
  }
  return valid;
}

/**
 * Flattened `attribution_*` properties for the existing lead properties bag.
 * Every value is bounded well under the 500-char property cap. Returns `{}`
 * when there is nothing valid to attach — attaching nothing is always safe.
 */
export function attributionProperties(
  attribution?: LeadAttribution | null,
): Record<string, string> {
  const a = attribution === undefined ? readLeadAttribution() : attribution;
  if (!a) return {};
  const valid = validate(a);
  if (!valid) return {};
  const props: Record<string, string> = {
    attribution_landing_path: valid.landing_path.slice(0, 500),
    attribution_captured_at: valid.captured_at.slice(0, 500),
    attribution_consent_version: String(valid.consent_version).slice(0, 500),
  };
  if (valid.consent_at) props.attribution_consent_at = valid.consent_at.slice(0, 500);
  for (const k of UTM_KEYS) {
    const v = valid[k];
    if (v) props[`attribution_${k}`] = v.slice(0, 500);
  }
  return props;
}

/** Short, honest one-line summary for the existing HubSpot note body. */
export function attributionSummary(attribution?: LeadAttribution | null): string {
  const a = attribution === undefined ? readLeadAttribution() : attribution;
  const valid = a ? validate(a) : null;
  if (!valid) return "";
  const bits = UTM_KEYS.filter((k) => valid[k]).map((k) => `${k.replace("utm_", "")}=${valid[k]}`);
  if (bits.length === 0) return "";
  return `Campaign (UTM only, from ${valid.landing_path}): ${bits.join(", ")}`;
}
